import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { SharedModule } from 'src/app/shared/shared.module';
import { CurriculumSessionService, SECTION_KEYS, SECTION_LABELS, SECTION_ICONS } from 'src/app/services/curriculum-session.service';
import { TtsStatusService } from 'src/app/shared/text-to-speech/tts-status.service';
import { TtsStatusChipComponent } from 'src/app/shared/text-to-speech/tts-status-chip/tts-status-chip.component';
import { TtsAudioPanelComponent } from 'src/app/shared/text-to-speech/tts-audio-panel/tts-audio-panel.component';

@Component({
  selector: 'app-curriculum-session-form',
  templateUrl: './curriculum-session-form.component.html',
  styleUrls: ['./curriculum-session-form.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule, TtsStatusChipComponent, TtsAudioPanelComponent],
  providers: [TtsStatusService],
})
export class CurriculumSessionFormComponent implements OnInit {
  @Input() mode: 'review' | 'editor' = 'editor';

  /**
   * Editor mode only. Pass null or omit for a new session; pass a session UUID
   * to load an existing session for editing.
   */
  @Input() sessionId: string | null = null;

  private router = inject(Router);
  private sessionService = inject(CurriculumSessionService);
  ttsStatus = inject(TtsStatusService);

  session: any = null;
  total = 0;
  loading = false;
  saving = false;
  isDirty = false;
  // Ids minted for stored key terms that had none. Until a save persists them,
  // content-api keys those terms' audio by position, so their chips use the index.
  private unsavedKeyTermIds = new Set<string>();
  emptyMandatoryFields: string[] = [];
  saveError: string | null = null;

  /** fieldPath whose audio panel is open; null when closed. */
  activeTtsFieldPath: string | null = null;

  savedWithPendingAudio = false;

  private snapshotDrafts = '';

  private snapshotOpenerKeyTermsDrafts = '';
  private snapshotPracticeDrafts = '';
  private snapshotCoreFields = '';

  // TSV paste mode (new sessions only)
  pasteMode = false;
  pasteText = '';
  pasteError: string | null = null;
  parsedUnitIntId: string | null = null;

  /** Result of a bulk auto-save (set after all rows from a multi-row paste have been saved). */
  bulkSaveResult: { success: number; total: number } | null = null;

  /**
   * ── Curriculum Google Sheet — TSV ingestion reference ────────────────────────
   *
   * Column order is fixed. Users paste data rows only (no header row required).
   * Rows are identified by a numeric value in the first column (Skill_order).
   * Cell values of '—' or '-' are normalised to empty string.
   *
   * COLUMN HANDLING SUMMARY
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * SAVED — mapped directly to API fields:
   *   Unit_uuid            → session.unitId
   *   Session              → session.sessionOrder  (parsed as integer)
   *   Guiding_question     → opener.guidingQuestion
   *   Key_term_title_1     → opener.keyTerms[0].title
   *   Key_term_definition_1→ opener.keyTerms[0].description
   *   Key_term_title_2     → opener.keyTerms[1].title
   *   Key_term_definition_2→ opener.keyTerms[1].description
   *   Discussion_prompt    → practice[0].prompt  (type: 'discussion')
   *   Role_play_scenario   → practice[1].prompt  (type: 'roleplay')
   *   Mini_game_direction  → practice[2].prompt  (type: 'miniGame')
   *   core_Content_id      → coreMaterial   (integer FK → Content.id, resolved to uuid)
   *   extend_Content_id    → extend         (integer FK → Content.id, resolved to uuid)
   *   Session_note         → session.sessionNote
   *   Session_note_tip     → session.sessionNoteTip
   *
   * IGNORED — present in the sheet but not yet consumed:
   *   Skill_order          → used only to identify data rows vs. header/group rows
   *   Goal_title, Goal_order
   *   Unit_title, Unit_order, Unit link
   *   flag_core_nonvideo, flag_extend_noninteractive, flag_notes
   *   core_Content_code, core_Content_id (already saved as coreMaterial above), core_UnitContent_id
   *   core_Content_title, core_Content_type, core_Content_description
   *   core_Subtitles       → closed-caption VTT URL (not the video playback URL)
   *   core_IA_json
   *   extend_Content_code, extend_Content_id (already saved as extend above), extend_UnitContent_id
   *   extend_Content_title, extend_Content_description, extend_Content_type, extend_Subtitles, extend_IA_json
   *   Session_note, Session_note_tip  → already saved as sessionNote / sessionNoteTip above
   *   ai_generated_at
   * ─────────────────────────────────────────────────────────────────────────────
   */
  private readonly TSV_HEADERS = [
    'Skill_order',
    'Goal_title',
    'Goal_order',
    'Unit_uuid',
    'Unit_title',
    'Unit_order',
    'Session',
    'Unit link',
    'flag_core_nonvideo',
    'flag_extend_noninteractive',
    'flag_notes',
    'core_Content_code',
    'core_Content_id',
    'core_UnitContent_id',
    'core_Content_title',
    'core_Content_type',
    'core_Content_description',
    'core_Subtitles',
    'core_IA_json',
    'extend_Content_code',
    'extend_Content_id',
    'extend_UnitContent_id',
    'extend_Content_title',
    'extend_Content_type',
    'extend_Content_description',
    'extend_Subtitles',
    'extend_IA_json',
    'Guiding_question',
    'Key_term_title_1',
    'Key_term_definition_1',
    'Key_term_title_2',
    'Key_term_definition_2',
    'Discussion_prompt',
    'Role_play_scenario',
    'Mini_game_direction',
    'Session_note',
    'Session_note_tip',
    'ai_generated_at',
  ];

  // Generic field drafts: sectionDrafts[sectionKey][fieldKey] = string value
  sectionDrafts: Record<string, Record<string, string>> = {};
  sectionFieldTypes: Record<string, Record<string, 'string' | 'array' | 'textarea' | 'json-array'>> = {};
  sectionErrors: Record<string, string> = {};

  // Typed drafts

  openerKeyTermsDrafts: Array<{ id: string; title: string; description: string }> = [];
  practiceDrafts: Array<{ type: string; title: string; prompt: string }> = [];

  readonly practiceTypes = ['discussion', 'roleplay', 'miniGame'] as const;

  sectionKeys = SECTION_KEYS;
  sectionLabels = SECTION_LABELS;
  sectionIcons = SECTION_ICONS;

  collapsedSections: Record<string, boolean> = {};

  get isNew(): boolean {
    return this.mode === 'editor' && !this.sessionId;
  }

  get isExistingEditor(): boolean {
    return this.mode === 'editor' && !!this.sessionId;
  }

  get pageTitle(): string {
    if (this.mode === 'review') {
      return 'Session Review Queue';
    }
    return this.isNew ? 'New Session' : 'Edit Session';
  }

  // Derived UI flags to keep template conditions readable.
  get showPasteEntryPoint(): boolean {
    return this.isNew && this.pasteMode;
  }

  get showPasteInputState(): boolean {
    return this.showPasteEntryPoint && !this.saving && !this.bulkSaveResult;
  }

  get showBulkPasteSuccessState(): boolean {
    return this.showPasteEntryPoint && !this.saving && !!this.bulkSaveResult;
  }

  get bulkSaveSuccessCount(): number {
    return this.bulkSaveResult?.success ?? 0;
  }

  get showReviewEmptyState(): boolean {
    return this.mode === 'review' && !this.loading && !this.session;
  }

  get showSessionView(): boolean {
    return !this.loading && !!this.session;
  }

  get showActionBar(): boolean {
    return !this.loading && (!!this.session || this.isNew) && !this.pasteMode;
  }

  ngOnInit(): void {
    if (this.mode === 'review') {
      this.loadNext();
    } else if (this.sessionId) {
      this.loadSession(this.sessionId);
    } else {
      // New session: start in paste mode; session is initialized after paste or manual switch
      this.pasteMode = true;
    }
  }

  // ── TSV paste flow ─────────────────────────────────────────────────────────────

  /** Skip paste and go straight to a blank form. */
  switchToManual(): void {
    this.pasteMode = false;
    this.bulkSaveResult = null;
    this.parsedUnitIntId = null;
    this.session = {
      sessionOrder: 1,
      unitId: '',
      clinicalApproved: false,
    };
    this.initDrafts();
  }

  resetBulkPasteState(): void {
    this.bulkSaveResult = null;
    this.pasteText = '';
  }

  /**
   * Parse a TSV paste from the curriculum Google Sheet.
   * Headers are hardcoded — users paste data rows only (no header row required).
   * If one row is found, the form is populated immediately.
   * If multiple rows are found, a row picker is shown.
   */
  parseTsv(): void {
    this.pasteError = null;
    this.bulkSaveResult = null;

    // Split into rows; keep only rows with a numeric value in the first column
    // (filters out blank lines, group header rows, and any accidentally pasted column headers)
    const dataRows = this.mergeMultilineRows(this.pasteText)
      .map(l => l.split('\t'))
      .filter(cols => {
        const first = cols[0]?.trim();
        return first && !isNaN(Number(first));
      });

    if (dataRows.length === 0) {
      this.pasteError = 'No data rows found. Paste one or more data rows from the sheet.';
      return;
    }

    if (dataRows.length === 1) {
      this.populateFromRow(dataRows[0]);
      return;
    }

    // Multiple rows — save all automatically
    this.saveAllRows(dataRows);
  }

  // Collapses pasted TSV text into one logical line per data row; ex, pasted html can contain line breaks.
  private mergeMultilineRows(text: string): string[] {
    const physicalLines = text.replace(/\r\n/g, '\n').split('\n');
    const rows: string[] = [];

    for (const line of physicalLines) {
      const firstCell = line.split('\t')[0]?.trim();
      const startsNewRow = !!firstCell && !isNaN(Number(firstCell));

      if (startsNewRow || rows.length === 0) {
        rows.push(line);
      } else {
        // Continue previous row: replace embedded newline with a space.
        rows[rows.length - 1] = `${rows[rows.length - 1]} ${line}`.trim();
      }
    }

    return rows;
  }

  /** Auto-saves all rows from a multi-row paste, then shows a bulk success message. */
  private saveAllRows(rows: string[][]): void {
    this.saving = true;
    this.saveError = null;

    const payloads = rows.map(row => this.buildPayloadFromRow(row));
    forkJoin(payloads.map(payload => this.sessionService.save(payload))).subscribe({
      next: () => {
        this.saving = false;
        this.bulkSaveResult = { success: rows.length, total: rows.length };
      },
      error: (err: any) => {
        this.saving = false;
        this.saveError = this.extractErrorMessage(err);
      },
    });
  }

  /**
   * Builds a save payload directly from a raw TSV row without touching component state.
   * Used by saveAllRows() for bulk saves.
   */
  private buildPayloadFromRow(row: string[]): any {
    const col = (name: string): string => {
      const idx = this.TSV_HEADERS.indexOf(name);
      if (idx < 0) {
        return '';
      }
      const raw = (row[idx] ?? '').trim();
      return raw === '—' || raw === '-' ? '' : raw;
    };

    const opener: any = {};
    const guidingQ = col('Guiding_question');
    if (guidingQ) {
      opener.guidingQuestion = guidingQ;
    }

    const keyTerms: Array<{ id: string; title: string; description: string }> = [];
    const kt1Title = col('Key_term_title_1');
    if (kt1Title) {
      keyTerms.push({ id: crypto.randomUUID(), title: kt1Title, description: col('Key_term_definition_1') });
    }
    const kt2Title = col('Key_term_title_2');
    if (kt2Title) {
      keyTerms.push({ id: crypto.randomUUID(), title: kt2Title, description: col('Key_term_definition_2') });
    }
    if (keyTerms.length > 0) {
      opener.keyTerms = keyTerms;
    }

    const discussion = col('Discussion_prompt');
    const roleplay = col('Role_play_scenario');
    const miniGame = col('Mini_game_direction');
    const hasPractice = discussion || roleplay || miniGame;

    return {
      unitId: col('Unit_uuid'),
      sessionOrder: parseInt(col('Session'), 10) || 1,
      clinicalApproved: false,
      opener: Object.keys(opener).length > 0 ? opener : null,
      coreMaterial: col('core_Content_id') ? parseInt(col('core_Content_id'), 10) : null,
      extend: col('extend_Content_id') ? parseInt(col('extend_Content_id'), 10) : null,
      sessionNote: col('Session_note') || null,
      sessionNoteTip: col('Session_note_tip') || null,
      practice: hasPractice
        ? [
            { type: 'discussion', title: 'Discussion', prompt: discussion },
            { type: 'roleplay', title: 'Role-Play', prompt: roleplay },
            { type: 'miniGame', title: 'Mini Game', prompt: miniGame },
          ]
        : null,
    };
  }

  /**
   * Populate the form from a single parsed data row.
   * Uses TSV_HEADERS for column lookups.
   */
  private populateFromRow(row: string[]): void {
    const col = (name: string): string => {
      const idx = this.TSV_HEADERS.indexOf(name);
      if (idx < 0) {
        return '';
      }
      const raw = (row[idx] ?? '').trim();
      return raw === '—' || raw === '-' ? '' : raw;
    };

    // Session core fields
    const unitUuid = col('Unit_uuid');
    this.session = {
      sessionOrder: parseInt(col('Session'), 10) || 1,
      unitId: unitUuid,
      clinicalApproved: false,
      sessionNote: col('Session_note') || null,
      sessionNoteTip: col('Session_note_tip') || null,
    };

    this.parsedUnitIntId = null; // Unit_uuid is now the source of truth

    // Build empty draft structure
    this.openerKeyTermsDrafts = [];
    this.sectionDrafts = {};
    this.sectionFieldTypes = {};
    for (const key of SECTION_KEYS) {
      this.sectionDrafts[key] = {};
      this.sectionFieldTypes[key] = {};
    }

    // coreMaterial: populate directly from core_Content_id (integer FK → Content.id)
    this.sectionDrafts['coreMaterial']['contentId'] = col('core_Content_id');
    this.sectionFieldTypes['coreMaterial']['contentId'] = 'string';

    // extend: populate directly from extend_Content_id (integer FK → Content.id)
    this.sectionDrafts['extend']['contentId'] = col('extend_Content_id');
    this.sectionFieldTypes['extend']['contentId'] = 'string';

    // Opener: guiding question
    const guidingQ = col('Guiding_question');
    if (guidingQ) {
      this.sectionDrafts['opener']['guidingQuestion'] = guidingQ;
      this.sectionFieldTypes['opener']['guidingQuestion'] = guidingQ.length > 60 ? 'textarea' : 'string';
    }

    // Opener: key terms (up to 2 from the sheet)
    const kt1Title = col('Key_term_title_1');
    const kt1Def = col('Key_term_definition_1');
    if (kt1Title) {
      this.openerKeyTermsDrafts.push({ id: crypto.randomUUID(), title: kt1Title, description: kt1Def });
    }
    const kt2Title = col('Key_term_title_2');
    const kt2Def = col('Key_term_definition_2');
    if (kt2Title) {
      this.openerKeyTermsDrafts.push({ id: crypto.randomUUID(), title: kt2Title, description: kt2Def });
    }

    // Practice: fixed 3-entry typed array
    this.practiceDrafts = [
      { type: 'discussion', title: 'Discussion', prompt: col('Discussion_prompt') },
      { type: 'roleplay', title: 'Role-Play', prompt: col('Role_play_scenario') },
      { type: 'miniGame', title: 'Mini Game', prompt: col('Mini_game_direction') },
    ];

    // Snapshot AFTER all population so isDirty starts as false
    this.snapshotDrafts = JSON.stringify(this.sectionDrafts);
    this.snapshotOpenerKeyTermsDrafts = JSON.stringify(this.openerKeyTermsDrafts);
    this.snapshotPracticeDrafts = JSON.stringify(this.practiceDrafts);
    this.snapshotCoreFields = this.serializeCoreFields();
    this.isDirty = false;

    this.pasteMode = false;
  }

  // ── Loading ────────────────────────────────────────────────────────────────────

  loadNext(afterSessionId?: string): void {
    this.loading = true;
    this.sectionErrors = {};
    this.emptyMandatoryFields = [];
    this.collapsedSections = {};
    this.sessionService.getNextUnreviewed(afterSessionId).subscribe((response: any) => {
      this.session = response.session;
      this.total = response.total;
      this.loading = false;
      if (this.session) {
        this.initDrafts();
        this.loadTtsStatus();
      }
    });
  }

  loadSession(sessionId: string): void {
    this.loading = true;
    this.sessionService.get(sessionId).subscribe(
      (response: any) => {
        this.session = response.session;
        this.loading = false;
        this.initDrafts();
        this.loadTtsStatus();
      },
      () => {
        this.loading = false;
        this.router.navigate(['/curriculum-sessions']);
      }
    );
  }

  // ── Draft initialisation ───────────────────────────────────────────────────────

  initDrafts(): void {
    this.sectionDrafts = {};
    this.sectionFieldTypes = {};
    this.openerKeyTermsDrafts = [];
    this.practiceDrafts = [];
    this.unsavedKeyTermIds = new Set<string>();

    for (const key of SECTION_KEYS) {
      const val = this.session[key];
      this.sectionDrafts[key] = {};
      this.sectionFieldTypes[key] = {};

      // ── practice: fixed typed array of 3 entries (discussion, roleplay, miniGame)
      if (key === 'practice') {
        const arr = Array.isArray(val) ? val : [];
        this.practiceDrafts = this.practiceTypes.map((type, i) => ({
          type,
          title: arr[i]?.title ?? '',
          prompt: arr[i]?.prompt ?? '',
        }));
        continue;
      }

      // ── coreMaterial: integer FK to Content.id (stored as uuid) ─────────────
      if (key === 'coreMaterial') {
        this.sectionDrafts['coreMaterial']['contentId'] = val != null ? String(val) : '';
        this.sectionFieldTypes['coreMaterial']['contentId'] = 'string';
        continue;
      }

      // ── extend: integer FK to Content.id (stored as uuid) ────────────────────
      if (key === 'extend') {
        this.sectionDrafts['extend']['contentId'] = val != null ? String(val) : '';
        this.sectionFieldTypes['extend']['contentId'] = 'string';
        continue;
      }

      // ── generic object sections ───────────────────────────────────────────────
      if (!val || typeof val !== 'object' || Array.isArray(val)) {
        continue;
      }

      for (const [fk, fv] of Object.entries(val)) {
        // opener keyTerms: extract as typed array draft, skip generic processing
        if (key === 'opener' && fk === 'keyTerms' && Array.isArray(fv)) {
          this.openerKeyTermsDrafts = (fv as any[]).map((kt: any) => ({
            id: kt.id ?? this.mintKeyTermId(),
            title: kt.title ?? '',
            description: kt.description ?? '',
          }));
          continue;
        }

        if (Array.isArray(fv)) {
          const first = fv[0];
          if (fv.length > 0 && typeof first === 'object' && first !== null) {
            this.sectionDrafts[key][fk] = JSON.stringify(fv, null, 2);
            this.sectionFieldTypes[key][fk] = 'json-array';
          } else {
            this.sectionDrafts[key][fk] = (fv as string[]).join('\n');
            this.sectionFieldTypes[key][fk] = 'array';
          }
        } else if (fv !== null && typeof fv === 'object') {
          this.sectionDrafts[key][fk] = JSON.stringify(fv, null, 2);
          this.sectionFieldTypes[key][fk] = 'json-array';
        } else if (typeof fv === 'string' && (fv as string).length > 60) {
          this.sectionDrafts[key][fk] = fv as string;
          this.sectionFieldTypes[key][fk] = 'textarea';
        } else {
          this.sectionDrafts[key][fk] = String(fv ?? '');
          this.sectionFieldTypes[key][fk] = 'string';
        }
      }
    }

    const minted = this.unsavedKeyTermIds;
    this.resetDirtyBaseline();
    this.unsavedKeyTermIds = minted;
    this.isDirty = minted.size > 0;
  }

  private mintKeyTermId(): string {
    const id = crypto.randomUUID();
    this.unsavedKeyTermIds.add(id);
    return id;
  }

  // ── Dirty tracking ─────────────────────────────────────────────────────────────

  // Also called after a save that keeps the author on the form, or checkDirty()
  // would keep comparing against the state the session had when it opened.
  private resetDirtyBaseline(): void {
    this.snapshotDrafts = JSON.stringify(this.sectionDrafts);
    this.snapshotOpenerKeyTermsDrafts = JSON.stringify(this.openerKeyTermsDrafts);
    this.snapshotPracticeDrafts = JSON.stringify(this.practiceDrafts);
    this.snapshotCoreFields = this.serializeCoreFields();
    this.unsavedKeyTermIds = new Set<string>();
    this.isDirty = false;
  }

  checkDirty(): void {
    this.isDirty =
      this.unsavedKeyTermIds.size > 0 ||
      this.serializeCoreFields() !== this.snapshotCoreFields ||
      JSON.stringify(this.sectionDrafts) !== this.snapshotDrafts ||
      JSON.stringify(this.openerKeyTermsDrafts) !== this.snapshotOpenerKeyTermsDrafts ||
      JSON.stringify(this.practiceDrafts) !== this.snapshotPracticeDrafts;
  }

  private serializeCoreFields(): string {
    return JSON.stringify({
      unitId: this.session?.unitId ?? '',
      sessionOrder: this.session?.sessionOrder ?? '',
    });
  }

  // ── Validation ─────────────────────────────────────────────────────────────────

  allFieldsAreValid(): boolean {
    this.emptyMandatoryFields = [];
    if (!this.session?.unitId) {
      this.emptyMandatoryFields.push('unitId');
    }
    if (!this.session?.sessionOrder || this.session.sessionOrder < 1) {
      this.emptyMandatoryFields.push('sessionOrder');
    }
    return this.emptyMandatoryFields.length === 0;
  }

  // ── Payload assembly ───────────────────────────────────────────────────────────

  private buildSectionData(): any {
    const sectionData: any = {};

    for (const key of SECTION_KEYS) {
      // ── coreMaterial: integer FK to Content.id (stored as uuid) ─────────────
      if (key === 'coreMaterial') {
        const id = parseInt(this.sectionDrafts['coreMaterial']?.['contentId'] ?? '', 10);
        sectionData[key] = !isNaN(id) && id > 0 ? id : null;
        continue;
      }

      // ── extend: integer FK to Content.id (stored as uuid) ────────────────────
      if (key === 'extend') {
        const id = parseInt(this.sectionDrafts['extend']?.['contentId'] ?? '', 10);
        sectionData[key] = !isNaN(id) && id > 0 ? id : null;
        continue;
      }

      // ── practice: fixed typed array of 3 entries ─────────────────────────────
      if (key === 'practice') {
        const hasContent = this.practiceDrafts.some(e => e.title.trim() || e.prompt.trim());
        sectionData['practice'] = hasContent ? this.practiceDrafts : null;
        continue;
      }

      // ── generic sections ──────────────────────────────────────────────────────
      const fields = this.sectionDrafts[key];
      // An opener holding only keyTerms has no generic fields, but its drafts still need saving.
      const hasKeyTerms = key === 'opener' && this.openerKeyTermsDrafts.length > 0;
      if (!hasKeyTerms && (!fields || Object.keys(fields).length === 0)) {
        sectionData[key] = this.session[key] ?? null;
        continue;
      }

      const obj: any = {};
      for (const [fk, fv] of Object.entries(fields ?? {})) {
        const t = this.sectionFieldTypes[key]?.[fk];
        if (t === 'array') {
          obj[fk] = fv
            .split('\n')
            .map((s: string) => s.trim())
            .filter(Boolean);
        } else if (t === 'json-array') {
          try {
            obj[fk] = JSON.parse(fv);
          } catch {
            obj[fk] = fv;
          }
        } else {
          obj[fk] = fv;
        }
      }

      // opener: merge keyTerms from typed draft
      if (key === 'opener') {
        const terms = this.openerKeyTermsDrafts.filter(kt => kt.title.trim());
        if (terms.length > 0) {
          obj.keyTerms = terms;
        }
      }

      sectionData[key] = Object.keys(obj).length > 0 ? obj : null;
    }

    return sectionData;
  }

  // ── Actions ────────────────────────────────────────────────────────────────────

  /** Review mode: save with clinicalApproved=true, then advance to next session. */
  approve(): void {
    if (!this.allFieldsAreValid()) {
      return;
    }
    this.sectionErrors = {};
    this.saveError = null;
    const sectionData = this.buildSectionData();
    this.saving = true;
    const payload = { ...this.session, ...sectionData, clinicalApproved: true };
    this.sessionService.save(payload).subscribe({
      next: () => {
        this.saving = false;
        this.loadNext(this.session.sessionId);
      },
      error: (err: any) => {
        this.saving = false;
        this.saveError = this.extractErrorMessage(err);
        console.error('Failed to approve session:', err);
      },
    });
  }

  /** Editor mode: save and return to list. */
  save(): void {
    if (!this.allFieldsAreValid()) {
      console.error('[save] validation failed. emptyMandatoryFields=', this.emptyMandatoryFields);
      return;
    }
    this.sectionErrors = {};
    this.saveError = null;
    this.savedWithPendingAudio = false;
    const sectionData = this.buildSectionData();
    this.saving = true;
    const payload = { ...this.session, ...sectionData };
    try {
      const obs = this.sessionService.save(payload);
      obs.subscribe({
        next: (response: any) => {
          this.saving = false;
          this.stayAfterSaveOrLeave(response?.session?.sessionId ?? this.session?.sessionId);
        },
        error: (err: any) => {
          this.saving = false;
          this.saveError = this.extractErrorMessage(err);
          console.error('[save] HTTP error:', err);
        },
      });
    } catch (e) {
      this.saving = false;
      this.saveError = 'An unexpected error occurred. Please try again.';
      console.error('[save] caught synchronous exception:', e);
    }
  }

  /** Editor mode only: stay put when the save enqueued audio, else go to the list. */
  private stayAfterSaveOrLeave(sessionId?: string): void {
    if (!sessionId) {
      this.router.navigate(['/curriculum-sessions']);
      return;
    }

    this.ttsStatus.load(sessionId).subscribe({
      next: () => {
        if (this.ttsStatus.hasPending()) {
          this.savedWithPendingAudio = true;
          this.resetDirtyBaseline();
          this.ttsStatus.startPolling();
        } else {
          this.router.navigate(['/curriculum-sessions']);
        }
      },
      // Unreadable status falls back to the pre-feature behaviour.
      error: () => this.router.navigate(['/curriculum-sessions']),
    });
  }

  private extractErrorMessage(err: any): string {
    // err.error.error  → specific API message  e.g. "A session with sessionOrder 1 already exists for this unit"
    // err.error.message → generic HTTP status  e.g. "Conflict", "Not Found"
    // err.message       → Angular's own message e.g. "Http failure response for ..."
    const msg = err?.error?.error || err?.error?.message || err?.message;
    if (Array.isArray(msg)) {
      return msg.join(' ');
    }
    return msg || 'An unexpected error occurred. Please try again.';
  }

  /** Review mode: advance to next session without saving. */
  skip(): void {
    this.loadNext(this.session.sessionId);
  }

  confirmDelete(): void {
    if (!confirm('Delete this session? This cannot be undone.')) {
      return;
    }
    this.sessionService.delete(this.session.sessionId).subscribe(() => {
      this.router.navigate(['/curriculum-sessions']);
    });
  }

  goBack(): void {
    this.router.navigate(['/curriculum-sessions']);
  }

  // ── Spoken audio (text-to-speech) ──────────────────────────────────────────────

  readonly ttsFieldLabels: Record<string, string> = {
    sessionNote: 'Session note',
    sessionNoteTip: 'Session note tip',
    'opener.guidingQuestion': 'Guiding question',
  };

  loadTtsStatus(): void {
    const sessionId = this.session?.sessionId;
    if (!sessionId) {
      return;
    }

    this.ttsStatus.load(sessionId).subscribe({
      next: () => this.ttsStatus.startPolling(),
      // An unreadable status must never block editing.
      error: () => {},
    });
  }

  keyTermFieldPath(kt: any, field: 'title' | 'description'): string | null {
    if (!kt?.id) {
      return null;
    }
    const key = this.unsavedKeyTermIds.has(kt.id) ? this.openerKeyTermsDrafts.indexOf(kt) : kt.id;
    return `opener.keyTerms[${key}].${field}`;
  }

  private keyTermForPathKey(key: string): any {
    const byId = this.openerKeyTermsDrafts.find((kt: any) => kt.id === key);
    if (byId) {
      return byId;
    }
    const byIndex = this.openerKeyTermsDrafts[Number(key)];
    return byIndex && this.unsavedKeyTermIds.has(byIndex.id) ? byIndex : undefined;
  }

  // Practice items are keyed by type, not a uuid.
  practiceFieldPath(type: string): string {
    return `practice[${type}].prompt`;
  }

  openTtsPanel(fieldPath: string): void {
    this.activeTtsFieldPath = fieldPath;
  }

  closeTtsPanel(): void {
    this.activeTtsFieldPath = null;
  }

  ttsLabelFor(fieldPath: string | null): string {
    if (!fieldPath) {
      return '';
    }
    if (this.ttsFieldLabels[fieldPath]) {
      return this.ttsFieldLabels[fieldPath];
    }

    const keyTerm = fieldPath.match(/^opener\.keyTerms\[(.+)\]\.(title|description)$/);
    if (keyTerm) {
      const index = this.openerKeyTermsDrafts.indexOf(this.keyTermForPathKey(keyTerm[1]));
      const position = index >= 0 ? index + 1 : '';
      return `Key term ${position} ${keyTerm[2]}`.replace('  ', ' ').trim();
    }

    const practice = fieldPath.match(/^practice\[(.+)\]\.prompt$/);
    if (practice) {
      return `${practice[1]} prompt`;
    }

    return fieldPath;
  }

  ttsTextFor(fieldPath: string | null): string {
    if (!fieldPath) {
      return '';
    }
    if (fieldPath === 'sessionNote') {
      return this.session?.sessionNote ?? '';
    }
    if (fieldPath === 'sessionNoteTip') {
      return this.session?.sessionNoteTip ?? '';
    }
    if (fieldPath === 'opener.guidingQuestion') {
      return this.sectionDrafts['opener']?.['guidingQuestion'] ?? '';
    }

    const keyTerm = fieldPath.match(/^opener\.keyTerms\[(.+)\]\.(title|description)$/);
    if (keyTerm) {
      const item = this.keyTermForPathKey(keyTerm[1]);
      return item ? item[keyTerm[2]] ?? '' : '';
    }

    const practice = fieldPath.match(/^practice\[(.+)\]\.prompt$/);
    if (practice) {
      const item = this.practiceDrafts.find(e => e.type === practice[1]);
      return item?.prompt ?? '';
    }

    return '';
  }

  get ttsAllReady(): boolean {
    return this.savedWithPendingAudio && !this.ttsStatus.hasPending();
  }

  // ── Key term helpers ───────────────────────────────────────────────────────────

  addKeyTerm(): void {
    this.openerKeyTermsDrafts = [...this.openerKeyTermsDrafts, { id: crypto.randomUUID(), title: '', description: '' }];
    this.checkDirty();
  }

  removeKeyTerm(i: number): void {
    this.openerKeyTermsDrafts = this.openerKeyTermsDrafts.filter((_, idx) => idx !== i);
    this.checkDirty();
  }

  // ── Section helpers ────────────────────────────────────────────────────────────

  toggleSection(key: string): void {
    this.collapsedSections[key] = !this.collapsedSections[key];
  }

  isSectionCollapsed(key: string): boolean {
    return !!this.collapsedSections[key];
  }
  isSectionEmpty(sectionKey: string): boolean {
    if (sectionKey === 'coreMaterial' || sectionKey === 'extend' || sectionKey === 'practice') {
      return false;
    }
    // If draft fields have been populated (e.g. from TSV parse), the section is not empty
    if (Object.keys(this.sectionDrafts[sectionKey] || {}).length > 0) {
      return false;
    }
    const val = this.session?.[sectionKey];
    if (Array.isArray(val)) {
      return val.length === 0;
    }
    return !val;
  }
}
